/**
 * Native ES module produced from the historical shared-scope controller.
 * Cross-domain dependencies are explicit imports; eager DOM wiring is isolated in the init function.
 */
import { openEventDialog, openEventPhotoViewer, openNoteUpload } from '../community/events-notes-navigator.runtime.js';
import { autopilotTodayCard, openAutopilotRun, openAutopilotSettings } from '../courses/courses.runtime.js';
import { openNotificationByID } from '../groups/groups-notifications.runtime.js';
import { openMaterialDialog, renderMarkdownLite } from '../knowledge/materials-attendance-wiki.runtime.js';
import { glassFlowHold, isGlassFlow, setGlassFlow, setGlassFlowHold } from '../onboarding/onboarding.runtime.js';
import { $, $$, API, api, escapeHTML, fmtDate, fmtDateTime, fmtTime, isAdmin, parseBody, rawRequest, refreshSession, state, superGrokMode, t, toast } from './core.runtime.js';
import { closeDialog, openDialog, transitionDialog } from './dialog-boot.runtime.js';
import { openScheduleDialog, showSchedule } from '../schedule/lesson.runtime.js';
import { canManageEvents, canManageGroup, canUploadNotes, currentGroupLabel, openGroupSwitcher } from '../session/session.runtime.js';
import { lessonType } from '../settings/settings-schedule.runtime.js';
import { renderContextSchedule } from '../shell/hubs.runtime.js';
import { androidDownloadPath, isAndroidWebClient, isKUniAndroidShell, isToday, morphGlassMenu, openGlassMenu, openGlobalSearch, requireGroup, resetHeadActions, setView } from '../shell/navigation.runtime.js';
import { openHomeworkDialog } from '../study/homework-ai.runtime.js';
import { openRequirementDialog, requirementValueText } from '../study/requirements.runtime.js';

/**
 * Same-origin GET download (cookies). Avoids blob: URLs that Android WebView
 * turns into ACTION_VIEW → «Нет приложения для этой ссылки».
 */
function triggerSameOriginFileDownload(apiPath, filename) {
  const href = apiPath.startsWith('http') ? apiPath : apiPath;
  const a = document.createElement('a');
  a.href = href;
  if (filename) a.setAttribute('download', filename);
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function syncAndroidAccessToken() {
  try {
    window.__kuniAccessToken = state.accessToken || '';
    if (state.accessToken && typeof window.KUniAndroidNative?.setAccessToken === 'function') {
      window.KUniAndroidNative.setAccessToken(state.accessToken);
    }
    if (state.accessToken && window.KUniBridge?.post) {
      window.KUniBridge.post('auth.session', { access_token: state.accessToken });
    }
  } catch { /* shell optional */ }
}

function setDownloadButtonGlow(mode) {
  // mode: 'busy' | 'done' | 'off'
  try {
    document.documentElement.classList.toggle('kuni-dl-busy', mode === 'busy');
    document.documentElement.classList.toggle('kuni-dl-done', mode === 'done');
    if (mode === 'done') {
      setTimeout(() => document.documentElement.classList.remove('kuni-dl-done'), 1800);
    }
    if (mode === 'off') {
      document.documentElement.classList.remove('kuni-dl-busy', 'kuni-dl-done');
    }
  } catch { /* ignore */ }
}

const androidDownloadFinalizeTimers = new Map();

function downloadJobKey(payload = {}) {
  return String(payload.jobId || payload.filename || 'active');
}

function clearDownloadFinalizeTimer(payload = {}) {
  const key = downloadJobKey(payload);
  const timer = androidDownloadFinalizeTimers.get(key);
  if (timer) clearTimeout(timer);
  androidDownloadFinalizeTimers.delete(key);
}

function scheduleDownloadProgressFallback(payload = {}) {
  const percent = Number(payload.percent ?? payload.progress ?? 0);
  if (!Number.isFinite(percent) || percent < 100) return;
  const key = downloadJobKey(payload);
  clearDownloadFinalizeTimer(payload);
  // Bridge v1 should emit downloads.done after the native layer flushes the file.
  // Older shells occasionally stop at a visible 100% without the terminal event;
  // don't leave the web UI glowing forever while we wait for that legacy shell.
  androidDownloadFinalizeTimers.set(key, setTimeout(() => {
    androidDownloadFinalizeTimers.delete(key);
    setDownloadButtonGlow('done');
  }, 2200));
}

export function wireAndroidBridgeUi() {
  if (!isKUniAndroidShell() || window.__kuniBridgeUiWired) return;
  window.__kuniBridgeUiWired = true;
  const onMsg = (msg) => {
    if (!msg?.type) return;
    if (msg.type === 'downloads.started' || msg.type === 'downloads.progress') {
      setDownloadButtonGlow('busy');
      if (msg.type === 'downloads.progress') scheduleDownloadProgressFallback(msg.payload || {});
    }
    if (msg.type === 'downloads.done') {
      clearDownloadFinalizeTimer(msg.payload || {});
      setDownloadButtonGlow('done');
      toast(msg.payload?.filename ? `Сохранено: ${msg.payload.filename}` : 'Файл сохранён в Загрузки/kUni');
    }
    if (msg.type === 'downloads.error') {
      clearDownloadFinalizeTimer(msg.payload || {});
      setDownloadButtonGlow('off');
      if (msg.payload?.message === 'offline') toast('Нет сети — скачивание недоступно');
      else toast(msg.payload?.message ? `Скачивание: ${msg.payload.message}` : 'Не удалось скачать файл');
    }
    if (msg.type === 'network.status') {
      const online = Boolean(msg.payload?.online);
      document.documentElement.classList.toggle('kuni-offline', !online);
      document.documentElement.dataset.kuniOffline = online ? '0' : '1';
      const el = $('#connection-state');
      if (el) el.textContent = online ? t('shell.sync.ok') : t('shell.sync.offline');
    }
  };
  try {
    if (window.KUniBridge?.on && !window.__kuniBridgeListenerBound) {
      window.__kuniBridgeListenerBound = true;
      window.KUniBridge.on(onMsg);
    }
  } catch { /* ignore */ }
  // Poll install of bridge hooks (injected after page load by shell).
  let tries = 0;
  const tick = () => {
    tries += 1;
    syncAndroidAccessToken();
    if (window.KUniBridge?.on && !window.__kuniBridgeListenerBound) {
      window.__kuniBridgeListenerBound = true;
      window.KUniBridge.on(onMsg);
    }
    if (tries < 20) setTimeout(tick, 400);
  };
  setTimeout(tick, 200);
}

export async function downloadAuthorized(path, filename) {
  const safeName = String(filename || 'kuni-download').trim() || 'kuni-download';
  // Normalize: accept '/notes/…/download' or full API path
  let rel = String(path || '').trim();
  if (rel.startsWith(API + '/')) rel = rel.slice(API.length);
  if (!rel.startsWith('/')) rel = `/${rel}`;
  const apiPath = androidDownloadPath(rel);

  if (!navigator.onLine && !(typeof window.KUniAndroidNative?.isOnline === 'function' && window.KUniAndroidNative.isOnline())) {
    toast('Нет сети — скачивание недоступно');
    throw new Error('offline');
  }

  // --- Android (hybrid shell OR in-app WebView): never create blob: URLs ---
  if (isAndroidWebClient()) {
    syncAndroidAccessToken();
    setDownloadButtonGlow('busy');
    try {
      const startBridgeDownload = () => {
        if (!window.KUniBridge?.post) return false;
        window.KUniBridge.post('downloads.start', {
          path: apiPath,
          filename: safeName,
          access_token: state.accessToken || undefined,
        });
        return true;
      };
      // Bridge v1 is the canonical path. It owns final file flush + terminal
      // downloads.done/error events; the old direct download() method is kept
      // only for shells that have not injected KUniBridge yet.
      if (startBridgeDownload()) return;
      // Wait briefly for bridge injection from WebView.onPageFinished.
      for (let i = 0; i < 8; i += 1) {
        await new Promise((r) => setTimeout(r, 120));
        syncAndroidAccessToken();
        if (startBridgeDownload()) return;
      }
      if (typeof window.KUniAndroidNative?.download === 'function') {
        window.KUniAndroidNative.download(apiPath, safeName);
        return;
      }
      // Last resort: same-origin GET with cookies → WebView DownloadListener gets
      // a real HTTP URL (never blob:) and can hand it to Android DownloadManager.
      triggerSameOriginFileDownload(apiPath, safeName);
      setTimeout(() => setDownloadButtonGlow('done'), 900);
      return;
    } catch (e) {
      setDownloadButtonGlow('off');
      throw e;
    }
  }

  // --- Desktop / normal browsers: blob download ---
  setDownloadButtonGlow('busy');
  try {
    let response = await rawRequest(rel);
    if (response.status === 401 && await refreshSession()) response = await rawRequest(rel);
    if (!response.ok) {
      const b = await parseBody(response);
      throw new Error(b?.error?.message || `HTTP ${response.status}`);
    }
    const blob = await response.blob();
    // Reject HTML error pages mis-served as "files"
    if (blob.type && blob.type.includes('text/html')) {
      throw new Error('Сервер вернул страницу вместо файла');
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = safeName;
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setDownloadButtonGlow('done');
  } catch (e) {
    setDownloadButtonGlow('off');
    throw e;
  }
}

function normalizeAuthorizedPath(path) {
  let rel = String(path || '').trim();
  if (rel.startsWith(API + '/')) rel = rel.slice(API.length);
  if (!rel.startsWith('/')) rel = `/${rel}`;
  return rel;
}

async function fetchAuthorizedFileBlob(path) {
  const rel=normalizeAuthorizedPath(path);
  let response=await rawRequest(rel);
  if(response.status===401&&await refreshSession())response=await rawRequest(rel);
  if(!response.ok){
    const body=await parseBody(response);
    throw new Error(body?.error?.message||`HTTP ${response.status}`);
  }
  const blob=await response.blob();
  if(blob.type&&blob.type.includes('text/html'))throw new Error('Сервер вернул страницу вместо файла');
  return blob;
}

function withQueryParam(path, key, value) {
  const raw = normalizeAuthorizedPath(path);
  const separator = raw.includes('?') ? '&' : '?';
  return `${raw}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
}

async function mountRasterizedPDF(previewPath, content, objectURLs) {
  if (!previewPath) throw new Error('Для мобильного PDF нет preview endpoint');
  const meta = await api(normalizeAuthorizedPath(previewPath));
  const rawPages = Number(meta?.pages || 0);
  if (!Number.isFinite(rawPages) || rawPages < 1) throw new Error('Не удалось определить страницы PDF');
  const pages = Math.min(500, Math.floor(rawPages));
  content.innerHTML = `<div class="kuni-pdf-mobile"><div class="kuni-pdf-mobile-meta">${pages} ${pages === 1 ? 'страница' : 'страниц'}</div><div class="kuni-pdf-pages">${Array.from({length: pages}, (_, i) => `<figure class="kuni-pdf-page" data-pdf-page="${i + 1}"><div class="kuni-pdf-page-loading"><span></span><b>${i + 1}</b></div></figure>`).join('')}</div></div>`;
  const pending = new Map();
  let observer = null;
  const loadPage = async (figure) => {
    const page = Number(figure?.dataset?.pdfPage || 0);
    if (!page || figure.dataset.loaded === '1' || pending.has(page)) return pending.get(page);
    figure.dataset.loaded = 'loading';
    const task = (async () => {
      try {
        const blob = await fetchAuthorizedFileBlob(withQueryParam(previewPath, 'page', page));
        const url = URL.createObjectURL(blob);
        objectURLs.add(url);
        const img = new Image();
        img.className = 'kuni-pdf-page-image';
        img.alt = `Страница ${page}`;
        img.decoding = 'async';
        img.src = url;
        await img.decode?.().catch(() => {});
        if (!figure.isConnected) return;
        figure.replaceChildren(img);
        figure.dataset.loaded = '1';
        observer?.unobserve(figure);
      } catch (error) {
        if (!figure.isConnected) return;
        figure.dataset.loaded = 'error';
        figure.innerHTML = `<div class="kuni-pdf-page-error"><strong>Страница ${page}</strong><span>${escapeHTML(error.message || 'Не удалось загрузить')}</span><button class="button small" type="button">Повторить</button></div>`;
        figure.querySelector('button')?.addEventListener('click', () => { figure.dataset.loaded = '0'; loadPage(figure); });
      } finally {
        pending.delete(page);
      }
    })();
    pending.set(page, task);
    return task;
  };
  const figures = [...content.querySelectorAll('[data-pdf-page]')];
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) loadPage(entry.target); });
    }, { root: content, rootMargin: '900px 0px', threshold: 0.01 });
    figures.forEach((figure) => observer.observe(figure));
  } else {
    figures.slice(0, Math.min(4, figures.length)).forEach(loadPage);
  }
  // First page must appear immediately, even before IntersectionObserver fires.
  await loadPage(figures[0]);
  return () => observer?.disconnect();
}

function filePreviewKind(filename='',mimeType='') {
  const name=String(filename||'').toLowerCase(),mime=String(mimeType||'').toLowerCase();
  const ext=(name.match(/\.([a-z0-9]+)$/i)?.[1]||'').toLowerCase();
  if(mime==='application/pdf'||ext==='pdf')return 'pdf';
  if(mime.startsWith('image/')||['png','jpg','jpeg','webp'].includes(ext))return 'image';
  if(mime==='text/markdown'||ext==='md')return 'markdown';
  if(mime.startsWith('text/')||ext==='txt')return 'text';
  if(mime.includes('wordprocessingml')||ext==='docx')return 'office';
  if(mime.includes('presentationml')||ext==='pptx')return 'office';
  if(mime.includes('spreadsheetml')||ext==='xlsx')return 'office';
  return 'unknown';
}

export async function openFileViewer({path,previewPath='',filename='Файл',mimeType='',title='',downloadPath=''}) {
  const kind=filePreviewKind(filename,mimeType),fileTitle=String(title||filename||'Файл'),downloadURL=downloadPath||path;
  if(kind==='image'){
    if(typeof openEventPhotoViewer==='function'){
      openEventPhotoViewer('',[{id:'file-preview',file_url:normalizeAuthorizedPath(path)}],0);
      return;
    }
  }
  if(kind==='pdf'){
    try{
      const {openPdfReader}=await import('../../features/file-viewer/PdfReader.jsx');
      const rel=normalizeAuthorizedPath(path);
      const pdfURL=/^https?:\/\//i.test(String(path||''))?String(path):`${API}${rel}`;
      openPdfReader({
        url:pdfURL,
        title:fileTitle,
        filename,
        getHttpHeaders:()=>state.accessToken?{Authorization:`Bearer ${state.accessToken}`}:{},
        refreshAuth:()=>refreshSession(),
        onDownload:async()=>{try{await downloadAuthorized(downloadURL,filename)}catch(error){toast(error.message);throw error}},
      });
      return;
    }catch(error){
      console.warn('[file-viewer] PDF.js reader failed to initialize; using compatibility fallback',error);
    }
  }
  const dialog=document.createElement('dialog');
  dialog.className='kuni-file-viewer';
  dialog.setAttribute('aria-label',`Просмотр файла ${filename}`);
  dialog.innerHTML=`<div class="kuni-file-viewer-shell"><header class="kuni-file-viewer-head"><div><strong>${escapeHTML(fileTitle)}</strong><small>${escapeHTML(filename)}</small></div><div class="kuni-file-viewer-actions"><button class="button small" type="button" data-file-viewer-download>Скачать</button><button class="kuni-file-viewer-close" type="button" data-file-viewer-close aria-label="Закрыть">×</button></div></header><main class="kuni-file-viewer-body"><div class="kuni-file-viewer-loading" data-file-viewer-loading><span></span><strong>Открываем файл…</strong></div><div class="kuni-file-viewer-content" data-file-viewer-content hidden></div></main></div>`;
  document.body.appendChild(dialog);
  const content=$('[data-file-viewer-content]',dialog),loading=$('[data-file-viewer-loading]',dialog);
  const objectURLs=new Set();
  let previewCleanup=()=>{};
  const cleanup=()=>{try{previewCleanup();}catch{};for(const url of objectURLs)URL.revokeObjectURL(url);objectURLs.clear();dialog.remove();};
  $('[data-file-viewer-close]',dialog).addEventListener('click',()=>dialog.close());
  $('[data-file-viewer-download]',dialog).addEventListener('click',()=>downloadAuthorized(downloadURL,filename).catch(error=>toast(error.message)));
  dialog.addEventListener('cancel',(event)=>{event.preventDefault();dialog.close();});
  dialog.addEventListener('close',cleanup,{once:true});
  dialog.addEventListener('click',(event)=>{if(event.target===dialog)dialog.close();});
  dialog.showModal();
  try{
    if(kind==='pdf'){
      if(isAndroidWebClient()||navigator.pdfViewerEnabled===false){
        previewCleanup=await mountRasterizedPDF(previewPath,content,objectURLs);
      }else{
        const blob=await fetchAuthorizedFileBlob(path),objectURL=URL.createObjectURL(blob);objectURLs.add(objectURL);
        content.innerHTML=`<iframe class="kuni-pdf-frame" title="${escapeHTML(filename)}" src="${escapeHTML(objectURL)}#toolbar=1&navpanes=0&view=FitH"></iframe>`;
      }
    }else if(kind==='image'){
      const blob=await fetchAuthorizedFileBlob(path),objectURL=URL.createObjectURL(blob);objectURLs.add(objectURL);
      content.innerHTML=`<div class="kuni-image-preview"><img src="${escapeHTML(objectURL)}" alt="${escapeHTML(filename)}"></div>`;
    }else if(kind==='text'||kind==='markdown'){
      const blob=await fetchAuthorizedFileBlob(path),text=await blob.text();
      content.innerHTML=kind==='markdown'?`<article class="kuni-markdown-preview">${renderMarkdownLite(text)}</article>`:`<pre class="kuni-text-preview">${escapeHTML(text)}</pre>`;
    }else if(kind==='office'&&previewPath){
      const preview=await api(normalizeAuthorizedPath(previewPath));
      content.innerHTML=`<div class="kuni-office-preview kuni-office-${escapeHTML(preview.kind||'document')}">${preview.html||'<p>Предпросмотр пуст.</p>'}</div>`;
    }else{
      content.innerHTML=`<div class="kuni-file-preview-unsupported"><strong>Этот формат нельзя показать внутри kUni</strong><p>Файл можно скачать отдельной кнопкой.</p><button class="button primary" type="button" data-file-viewer-fallback-download>Скачать файл</button></div>`;
      $('[data-file-viewer-fallback-download]',content)?.addEventListener('click',()=>downloadAuthorized(downloadURL,filename).catch(error=>toast(error.message)));
    }
    loading.hidden=true;content.hidden=false;
  }catch(error){
    loading.hidden=true;content.hidden=false;
    content.innerHTML=`<div class="kuni-file-preview-unsupported"><strong>Не удалось открыть предпросмотр</strong><p>${escapeHTML(error.message||'Неизвестная ошибка')}</p><button class="button primary" type="button" data-file-viewer-fallback-download>Скачать файл</button></div>`;
    $('[data-file-viewer-fallback-download]',content)?.addEventListener('click',()=>downloadAuthorized(downloadURL,filename).catch(err=>toast(err.message)));
  }
}

export function simpleDeadline(value) {
  if (!value) return 'Без срока';
  const due = new Date(value); const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const days = Math.round((dueDay - today) / 86400000);
  if (days < 0) return 'Срок прошёл';
  if (days === 0) return `Сегодня · ${fmtTime(due)}`;
  if (days === 1) return `Завтра · ${fmtTime(due)}`;
  return fmtDate(due, { day:'numeric', month:'short' });
}

export function lessonMoment(entry, current) {
  if (!entry) return '';
  if (current) return `Сейчас · до ${fmtTime(entry.ends_at)}`;
  const starts = new Date(entry.starts_at);
  if (isToday(starts)) return `Сегодня · ${fmtTime(starts)}`;
  return `${fmtDate(starts,{weekday:'short',day:'numeric',month:'short'})} · ${fmtTime(starts)}`;
}

export function mountSimpleViewLinks(root = document) {
  $$('[data-open-simple-view]', root).forEach((button) => button.addEventListener('click', () => setView(button.dataset.openSimpleView)));
}

export function openSimpleAddMenu(fromEl = null) {
  const useGlass = superGrokMode() || isGlassFlow();
  const actions = [];
  if (canManageGroup()) actions.push({ id: 'homework', label: 'Задание', meta: 'Выбрать пару и добавить задание', run: () => openHomeworkDialog() });
  if (canManageGroup()) actions.push({ id: 'requirement', label: 'Автомат', meta: 'Выбрать предмет и задать условия', run: () => openRequirementDialog() });
  if (canUploadNotes()) actions.push({ id: 'note', label: 'Конспект', meta: 'Выбрать пару и загрузить файл', run: () => openNoteUpload(null,'note') });
  if (canUploadNotes()) actions.push({ id: 'solution', label: 'Решение', meta: 'Добавить готовое решение задачи', run: () => openNoteUpload(null,'solution') });
  if (canManageGroup()) actions.push({ id: 'material', label: 'Материал', meta: 'Добавить ссылку или файл по предмету', run: () => openMaterialDialog() });
  if (canManageGroup()) actions.push({ id: 'schedule', label: 'Пара', meta: 'Добавить занятие в расписание', run: () => openScheduleDialog() });
  if (canManageEvents()) actions.push({ id: 'event', label: 'Мероприятие', meta: 'Опубликовать событие для студентов', run: () => openEventDialog() });

  // Number only the actions the current user can actually see. No gaps and no mixed ✓/digits.
  actions.forEach((action, index) => { action.icon = String(index + 1); });

  const glassItems = actions.map((a) => ({
    ...a,
    stayInGlass: true,
    run: () => {
      setGlassFlow(true);
      setGlassFlowHold(true);
      return a.run();
    },
  }));

  if (useGlass) {
    const dialog = $('#dialog');
    const canMorph = dialog?.open
      && dialog.classList.contains('sg-glass-menu')
      && dialog.querySelector('.sg-orb-sheet-panel');
    if (canMorph) {
      morphGlassMenu({
        kicker: 'Создание',
        title: 'Что добавить?',
        showOrb: true,
        items: glassItems,
        fromEl: fromEl || dialog.querySelector('[data-glass-action="add"]'),
      });
      return;
    }
    openGlassMenu({
      kicker: 'Создание',
      title: 'Что добавить?',
      showOrb: true,
      items: glassItems,
    });
    return;
  }

  openDialog('Что добавить?', `<div class="sg-orb-sheet simple-add-sheet">${actions.map((a, index) => `<button type="button" class="sg-orb-action" data-simple-add="${a.id}"><b>${index + 1}</b><span><strong>${escapeHTML(a.label)}</strong><small>${escapeHTML(a.meta)}</small></span><i>→</i></button>`).join('')}</div>`, [{ label: 'Закрыть', action: closeDialog }]);
  $$('[data-simple-add]').forEach((button) => button.addEventListener('click', () => {
    const action = actions.find((item) => item.id === button.dataset.simpleAdd);
    if (!action) return;
    transitionDialog(() => action.run());
  }));
}

export function simpleTimeUntil(value,now=new Date()) {
  const minutes=Math.max(0,Math.round((new Date(value)-now)/60000));
  if(minutes<60)return `через ${minutes||1} мин`;
  const hours=Math.floor(minutes/60),rest=minutes%60;return rest?`через ${hours} ч ${rest} мин`:`через ${hours} ч`;
}

export function uiIcon(name, size = 20) {
  const paths = {
    home:'<path d="M3 11.5 12 4l9 7.5M5.5 10v10h13V10M9 20v-6h6v6"/>',
    calendar:'<path d="M7 3v3M17 3v3M4 9h16M5 5h14a1 1 0 0 1 1 1v14H4V6a1 1 0 0 1 1-1Z"/>',
    book:'<path d="M4 5.5A3.5 3.5 0 0 1 7.5 2H20v17H7.5A3.5 3.5 0 0 0 4 22V5.5Z"/>',
    more:'<path d="M5 12h.01M12 12h.01M19 12h.01"/>',
    search:'<circle cx="11" cy="11" r="6.5"/><path d="m16 16 4.2 4.2"/>',
    plus:'<path d="M12 5v14M5 12h14"/>',
    chevron:'<path d="m9 6 6 6-6 6"/>',
    back:'<path d="m15 18-6-6 6-6"/>',
    check:'<path d="m5 12 4 4L19 6"/>',
    circle:'<circle cx="12" cy="12" r="8"/>',
    file:'<path d="M6 2h8l4 4v16H6zM14 2v5h5"/>',
    link:'<path d="M10 13a5 5 0 0 0 7.1 0l2-2a5 5 0 0 0-7.1-7.1l-1.1 1.1M14 11a5 5 0 0 0-7.1 0l-2 2A5 5 0 0 0 12 20.1l1.1-1.1"/>',
    clock:'<circle cx="12" cy="12" r="9"/><path d="M12 7v6l4 2"/>',
    people:'<path d="M8 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm8-1a3 3 0 1 0 0-6M2 21v-2a6 6 0 0 1 12 0v2M14 15a5 5 0 0 1 8 4v2"/>',
    progress:'<path d="M4 19V9M10 19V5M16 19v-7M22 19H2"/>',
    edit:'<path d="m4 20 4.2-1 10-10a2.4 2.4 0 0 0-3.4-3.4l-10 10L4 20Z"/>',
    dots:'<path d="M5 12h.01M12 12h.01M19 12h.01"/>',
    tasks:'<path d="M9 6h11M9 12h11M9 18h11"/><path d="m3.5 6 1.5 1.5L7.5 5M3.5 12 5 13.5l2.5-2.5M3.5 18 5 19.5l2.5-2.5"/>',
    solution:'<path d="M6 3h9l3 3v15H6zM15 3v4h4"/><path d="m9 14 2 2 4-5"/>',
    target:'<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3"/><path d="M12 2v3M22 12h-3M12 22v-3M2 12h3"/>',
    skip:'<circle cx="12" cy="12" r="9"/><path d="m6 18 12-12"/>',
    attendance:'<path d="M5 4h14v16H5zM8 8h8M8 12h4"/><path d="m13 16 1.5 1.5L18 14"/>',
    pin:'<path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2.5"/>',
    star:'<path d="m12 3 2.7 5.5 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1-4.4-4.3 6.1-.9L12 3Z"/>',
    message:'<path d="M4 5h16v12H9l-5 4V5Z"/><path d="M8 9h8M8 13h5"/>',
    teacher:'<circle cx="9" cy="8" r="3"/><path d="M3 20v-2a6 6 0 0 1 12 0v2M16 5h5M18.5 2.5v5"/>',
  };
  return `<svg class="x10-icon" width="${size}" height="${size}" viewBox="0 0 24 24" aria-hidden="true">${paths[name] || paths.circle}</svg>`;
}

export function contextSkeleton() {
  return `<div class="x10-skeleton" aria-label="Загрузка"><div class="x10-sk-line w35"></div><div class="x10-sk-line w62 lg"></div><div class="x10-sk-block"></div><div class="x10-sk-line w22"></div><div class="x10-sk-row"></div><div class="x10-sk-row"></div><div class="x10-sk-row"></div></div>`;
}

export function contextEmpty(text) {
  return `<div class="x10-empty">${escapeHTML(text)}</div>`;
}

export function contextSection(title, count, body, extra = '') {
  const empty = typeof body === 'string' && body.includes('class="x10-empty"');
  return `<section class="x10-section${empty ? ' is-empty' : ''}"><header><div><strong>${escapeHTML(title)}</strong>${Number.isFinite(count) ? `<span>${count}</span>` : ''}</div>${extra}</header><div class="x10-section-body">${body}</div></section>`;
}

export function contextRow({icon='circle', title='', meta='', end='→', attrs='', className=''}) {
  return `<button class="x10-row ${className}" type="button" ${attrs}><span class="x10-row-icon">${uiIcon(icon,18)}</span><span class="x10-row-copy"><strong>${escapeHTML(title)}</strong>${meta?`<small>${escapeHTML(meta)}</small>`:''}</span><span class="x10-row-end">${end === '→' ? uiIcon('chevron',17) : escapeHTML(end)}</span></button>`;
}

function contextAddActions({entry=null, nextEntry=null, course=null} = {}) {
  const actions=[];
  const lesson = nextEntry || entry || course?.nextLesson || null;
  if(canManageGroup()) actions.push(['homework','Задание',lesson?'Пара и предмет уже выбраны':'Выбрать пару']);
  if(canUploadNotes()) actions.push(['note','Конспект',lesson?'Привязать к текущему контексту':'Выбрать пару']);
  if(canUploadNotes()) actions.push(['solution','Решение',lesson?'Привязать к текущему предмету':'Выбрать пару']);
  if(canManageGroup()) actions.push(['requirement','Автомат',(lesson||course)?'Условия для этого предмета':'Выбрать предмет']);
  if(canManageGroup()) actions.push(['material','Материал',(course||entry)?'Предмет уже выбран':'Добавить файл или ссылку']);
  if(!entry&&!course&&canManageGroup()) actions.push(['schedule','Пара','Добавить занятие в расписание']);
  if(!entry&&!course&&canManageEvents()) actions.push(['event','Мероприятие','Опубликовать событие']);
  return actions;
}

function openContextAddMenu(options = {}) {
  const actions=contextAddActions(options);
  if(!actions.length){toast('У вас нет доступных действий добавления');return;}
  openDialog('Добавить', `<div class="x10-action-list">${actions.map(([id,title,meta])=>contextRow({icon:id==='homework'?'check':id==='note'?'file':id==='solution'?'check':id==='material'?'link':'progress',title,meta,attrs:`data-x10-add="${id}"`})).join('')}</div>`, []);
  $$('[data-x10-add]').forEach((button)=>button.addEventListener('click',()=>{
    const id=button.dataset.x10Add;
    const targetLesson=options.nextEntry||options.entry||options.course?.nextLesson||null;
    const noteLesson=options.entry||options.course?.nextLesson||null;
    transitionDialog(() => {
      if(id==='homework') return targetLesson?openHomeworkDialog(null,targetLesson):openHomeworkDialog();
      if(id==='note') return noteLesson?openNoteUpload(noteLesson,'note'):openNoteUpload(null,'note');
      if(id==='solution') return noteLesson?openNoteUpload(noteLesson,'solution'):openNoteUpload(null,'solution');
      if(id==='requirement') return targetLesson?openRequirementDialog(null,targetLesson):openRequirementDialog();
      if(id==='material') return openMaterialDialog(null,options.course?.id||options.entry?.subject_id||'');
      if(id==='schedule') return openScheduleDialog();
      if(id==='event') return openEventDialog();
    });
  }));
}

export function mountContextTopActions(root, options = {}) {
  root.querySelector('[data-x10-search]')?.addEventListener('click',()=>openGlobalSearch().catch((e)=>toast(e.message)));
  root.querySelector('[data-x10-add]')?.addEventListener('click',()=>openContextAddMenu(options));
}

export async function renderSuperGrokHome() {
  resetHeadActions();
  if (!requireGroup()) return;
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const to = new Date(from); to.setDate(to.getDate() + 2);
  const [schedule, homework, requirements, autopilot, unread] = await Promise.all([
    api(`/groups/${state.groupID}/schedule?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`).catch(() => []),
    api(`/groups/${state.groupID}/homework?include_archived=false`).catch(() => []),
    api(`/groups/${state.groupID}/requirements?include_archived=false`).catch(() => []),
    api(`/autopilot/today?group_id=${encodeURIComponent(state.groupID)}`).catch(() => ({ enabled_subjects: 0, scheduled: [], recent: [] })),
    api('/notifications?unread_only=true').catch(() => []),
  ]);
  const entries = [...schedule].filter((e) => e.status !== 'cancelled').sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
  const current = entries.find((e) => new Date(e.starts_at) <= now && new Date(e.ends_at) > now) || null;
  const next = entries.find((e) => new Date(e.starts_at) > now) || null;
  const homeworkTasks = homework.filter((v) => v.status === 'active' && v.progress !== 'done').sort((a, b) => new Date(a.due_at || '2999-12-31') - new Date(b.due_at || '2999-12-31')).slice(0, 5);
  const reqTasks = requirements.flatMap((r) => (r.items || []).filter((i) => i.required && !i.completed).map((i) => ({ title: i.title, subject: r.subject_name || r.title, entry: '', meta: requirementValueText(i) }))).slice(0, Math.max(0, 5 - homeworkTasks.length));
  const tasks = [
    ...homeworkTasks.map((v) => ({ title: v.title, meta: `${v.subject_name || 'Задание'} · ${simpleDeadline(v.due_at)}`, entry: v.schedule_entry_id || '', view: 'homework', urgent: v.due_at && new Date(v.due_at).getTime() < Date.now() + 86400000 })),
    ...reqTasks.map((v) => ({ title: v.title, meta: `${v.subject} · ${v.meta}`, entry: v.entry, view: 'requirements', urgent: false })),
  ];
  const focus = current || next;
  const name = escapeHTML(state.user?.display_name?.split(' ')[0] || 'Привет');
  const focusCard = focus
    ? `<button class="sg-focus ${current ? 'is-live' : 'is-next'}" type="button" data-open-lesson="${focus.id}"><div class="sg-focus-top"><span>${current ? 'Сейчас' : 'Дальше'}</span><time>${current ? `до ${fmtTime(focus.ends_at)}` : `${fmtTime(focus.starts_at)} · ${escapeHTML(simpleTimeUntil(focus.starts_at, now))}`}</time></div><h1>${escapeHTML(focus.subject_name)}</h1><p>${escapeHTML([lessonType(focus.lesson_type), focus.teacher_name, focus.room_name].filter(Boolean).join(' · '))}</p><footer><span>Открыть пару</span><b>→</b></footer></button>`
    : `<div class="sg-focus is-free"><div class="sg-focus-top"><span>Сейчас</span><time>${fmtTime(now)}</time></div><h1>Свободно</h1><p>Ближайших пар нет — можно закрыть дела.</p><footer><span>${entries.filter((e) => isToday(e.starts_at)).length ? `Сегодня ещё будут пары` : 'Сегодня без пар'}</span></footer></div>`;
  const nextStrip = current && next
    ? `<button class="sg-next-strip" type="button" data-open-lesson="${next.id}"><span>Потом</span><strong>${escapeHTML(next.subject_name)}</strong><small>${fmtTime(next.starts_at)} · ${escapeHTML(simpleTimeUntil(next.starts_at, now))}</small><b>→</b></button>`
    : '';
  const taskHTML = tasks.length
    ? tasks.map((task) => `<button class="sg-task" type="button" data-task-view="${task.view}" ${task.entry ? `data-task-lesson="${task.entry}"` : ''}><i class="${task.urgent ? 'urgent' : ''}"></i><div><strong>${escapeHTML(task.title)}</strong><small>${escapeHTML(task.meta)}</small></div><b>→</b></button>`).join('')
    : `<div class="sg-empty success"><strong>Всё готово</strong><span>На ближайшее время дел нет.</span></div>`;
  const changes = (unread || []).slice(0, 3);
  const changesHTML = changes.length
    ? changes.map((item) => `<button class="sg-change" type="button" data-notification-id="${item.id}"><i class="${escapeHTML(item.severity || 'info')}"></i><div><strong>${escapeHTML(item.title)}</strong><small>${escapeHTML(item.body || '')}</small></div><b>→</b></button>`).join('')
    : `<div class="sg-empty"><strong>Тихо</strong><span>Новых изменений нет.</span></div>`;
  const root = $('#content');
  root.innerHTML = `<div class="sg-home">
    <header class="sg-home-head"><div><span class="sg-date">${fmtDate(now, { weekday: 'long', day: 'numeric', month: 'long' })}</span><h2>${name}</h2></div><button class="sg-group-chip" type="button" data-switch-group><small>Группа</small><strong data-current-group-label>${escapeHTML(currentGroupLabel())}</strong></button></header>
    ${focusCard}${nextStrip}
    <section class="sg-block sg-autopilot"><header><div><span>После пары</span><strong>Автопилот</strong></div><button type="button" data-open-autopilot>Настроить</button></header>${autopilotTodayCard(autopilot)}</section>
    <section class="sg-block"><header><div><span>Нужно сделать</span><strong>${tasks.length || 0}</strong></div><button type="button" data-sg-view="homework">Все</button></header><div class="sg-stack">${taskHTML}</div></section>
    <section class="sg-block"><header><div><span>Изменения</span><strong>${changes.length || 0}</strong></div><button type="button" data-sg-view="notifications">Все</button></header><div class="sg-stack">${changesHTML}</div></section>
  </div>`;
  $$('[data-open-lesson]', root).forEach((b) => b.addEventListener('click', () => showSchedule(b.dataset.openLesson)));
  $$('[data-task-view]', root).forEach((b) => b.addEventListener('click', () => (b.dataset.taskLesson ? showSchedule(b.dataset.taskLesson) : setView(b.dataset.taskView))));
  $$('[data-sg-view]', root).forEach((b) => b.addEventListener('click', () => setView(b.dataset.sgView)));
  $$('[data-open-autopilot]', root).forEach((b) => b.addEventListener('click', () => openAutopilotSettings().catch((e) => toast(e.message))));
  $$('[data-autopilot-run]', root).forEach((b) => b.addEventListener('click', () => openAutopilotRun(b).catch((e) => toast(e.message))));
  $$('[data-notification-id]', root).forEach((b) => b.addEventListener('click', () => openNotificationByID(b.dataset.notificationId, changes).catch((e) => toast(e.message))));
  $('[data-switch-group]', root)?.addEventListener('click', () => openGroupSwitcher());
  mountLessonFocusShimmer(root);
}

/** Pair-context card: shine only while scrolling down (no idle motion). */
function mountLessonFocusShimmer(root = document) {
  const cards = $$('.sg-focus:not(.is-free), button.x10-now', root);
  if (!cards.length) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const scroller = $('#content') || document.scrollingElement || document.documentElement;
  let ticking = false;
  let calmTimer = 0;
  let lastY = scroller.scrollTop || 0;
  // Soft continuous map: ~0.22 px shine per 1 px scroll — visible while scrolling, not two jumps.
  let travel = -40;
  const paint = (scrollingDown) => {
    ticking = false;
    const y = scroller.scrollTop || 0;
    const dy = y - lastY;
    lastY = y;
    if (scrollingDown && dy > 0) {
      travel = ((travel + dy * 0.22 + 40) % 140) - 40; // -40 … 100
      const hue = ((y * 0.05) % 10) - 3;
      cards.forEach((card) => {
        card.style.setProperty('--sg-shine', `${travel.toFixed(1)}%`);
        card.style.setProperty('--sg-hue', `${hue.toFixed(1)}deg`);
        card.style.setProperty('--sg-shine-opacity', '0.72');
        card.classList.add('is-scrolling');
      });
      clearTimeout(calmTimer);
      calmTimer = setTimeout(() => {
        cards.forEach((card) => {
          card.classList.remove('is-scrolling');
          card.style.setProperty('--sg-shine-opacity', '0');
        });
      }, 420);
    }
  };
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    const y = scroller.scrollTop || 0;
    const down = y > lastY;
    requestAnimationFrame(() => paint(down));
  };
  scroller.addEventListener('scroll', onScroll, { passive: true });
  if (scroller._sgShimmerCleanup) scroller._sgShimmerCleanup();
  scroller._sgShimmerCleanup = () => {
    scroller.removeEventListener('scroll', onScroll);
    clearTimeout(calmTimer);
  };
  cards.forEach((card) => {
    card.style.setProperty('--sg-shine', '-40%');
    card.style.setProperty('--sg-shine-opacity', '0');
  });
}

function stripSuperGrokHubChrome(root) {
  if (!root) return;
  root.classList.add('sg-page', 'sg-hub');
  // Hubs are the navigation — no second title/search row (see screens/5.png).
  root.querySelector('.x10-page-head')?.remove();
}

export async function renderSuperGrokStudyHub() {
  await renderContextStudyHub();
  stripSuperGrokHubChrome($('#content .x10-page'));
}

export async function renderSuperGrokMoreHub() {
  await renderContextMoreHub();
  stripSuperGrokHubChrome($('#content .x10-page'));
}

export async function renderSuperGrokSchedule() {
  await renderContextSchedule();
  $('#content .x10-page')?.classList.add('sg-page');
}

export async function renderContextHome() {
  resetHeadActions();
  if(!requireGroup()) return;
  const now=new Date(), from=new Date(now.getFullYear(),now.getMonth(),now.getDate()), to=new Date(from); to.setDate(to.getDate()+2);
  const [schedule,homework,requirements,autopilot,unread]=await Promise.all([
    api(`/groups/${state.groupID}/schedule?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`).catch(()=>[]),
    api(`/groups/${state.groupID}/homework?include_archived=false`).catch(()=>[]),
    api(`/groups/${state.groupID}/requirements?include_archived=false`).catch(()=>[]),
    api(`/autopilot/today?group_id=${encodeURIComponent(state.groupID)}`).catch(()=>({enabled_subjects:0,scheduled:[],recent:[]})),
    api('/notifications?unread_only=true').catch(()=>[]),
  ]);
  const entries=[...schedule].filter((e)=>e.status!=='cancelled').sort((a,b)=>new Date(a.starts_at)-new Date(b.starts_at));
  const current=entries.find((e)=>new Date(e.starts_at)<=now&&new Date(e.ends_at)>now)||null;
  const next=entries.find((e)=>new Date(e.starts_at)>now)||null;
  const homeworkTasks=homework.filter((v)=>v.status==='active'&&v.progress!=='done').sort((a,b)=>new Date(a.due_at||'2999-12-31')-new Date(b.due_at||'2999-12-31')).slice(0,5);
  const reqTasks=requirements.flatMap((r)=>(r.items||[]).filter((i)=>i.required&&!i.completed).map((i)=>({title:i.title,subject:r.subject_name||r.title,entry:'',meta:requirementValueText(i)}))).slice(0,Math.max(0,5-homeworkTasks.length));
  const focus=current||next;
  const focusHTML=focus?`<button class="x10-now ${current?'is-current':''}" type="button" data-open-lesson="${focus.id}"><span class="x10-eyebrow">${current?'Сейчас':'Следующая пара'}</span><div class="x10-now-main"><div><h1>${escapeHTML(focus.subject_name)}</h1><p>${escapeHTML([lessonType(focus.lesson_type),focus.teacher_name,focus.room_name].filter(Boolean).join(' · '))}</p></div><time><strong>${fmtTime(focus.starts_at)}</strong><span>${current?`до ${fmtTime(focus.ends_at)}`:isToday(focus.starts_at)?'сегодня':fmtDate(focus.starts_at,{weekday:'short',day:'numeric',month:'short'})}</span></time></div><span class="x10-open-hint">Открыть контекст пары ${uiIcon('chevron',16)}</span></button>`:`<div class="x10-now is-free"><span class="x10-eyebrow">Сейчас</span><div class="x10-now-main"><div><h1>Свободно</h1><p>Ближайших занятий нет.</p></div><time><strong>${fmtTime(now)}</strong></time></div></div>`;
  const tasks=[...homeworkTasks.map((v)=>({title:v.title,meta:`${v.subject_name||'Задание'} · ${simpleDeadline(v.due_at)}`,entry:v.schedule_entry_id||'',view:'homework'})),...reqTasks.map((v)=>({title:v.title,meta:`${v.subject} · ${v.meta}`,entry:v.entry,view:'requirements'}))];
  const tasksHTML=tasks.length?tasks.map((v)=>contextRow({icon:'circle',title:v.title,meta:v.meta,attrs:`data-x10-task="${v.view}" ${v.entry?`data-entry="${v.entry}"`:''}`})).join(''):contextEmpty('На ближайшее время ничего не требует внимания.');
  const changes=(unread||[]).slice(0,3);
  const changesHTML=changes.length?changes.map(item=>`<button class="v12-change-row" type="button" data-notification-id="${item.id}"><span class="v12-change-dot ${escapeHTML(item.severity||'info')}"></span><span><strong>${escapeHTML(item.title)}</strong><small>${escapeHTML(item.body||'')} · ${fmtDateTime(item.created_at)}</small></span><b>→</b></button>`).join(''):contextEmpty('Новых изменений нет.');
  const root=$('#content');
  root.innerHTML=`<div class="x10-page x10-home v12-today"><header class="x10-page-head"><div><span class="x10-eyebrow">${fmtDate(now,{weekday:'long',day:'numeric',month:'long'})}</span><h2>${escapeHTML(state.user?.display_name?.split(' ')[0]||'Привет')}</h2></div><div class="x10-page-tools"><button class="x10-icon-button" type="button" data-x10-search aria-label="Поиск">${uiIcon('search')}</button>${(canManageGroup()||canUploadNotes())?`<button class="x10-primary-icon" type="button" data-x10-add aria-label="Добавить">${uiIcon('plus')}</button>`:''}</div></header>${focusHTML}<section class="v12-today-block"><header><span>После пары</span><button type="button" data-open-autopilot>Настроить</button></header>${autopilotTodayCard(autopilot)}</section>${contextSection('Нужно сделать',tasks.length,tasksHTML,`<button class="x10-text-action" data-x10-view="homework" type="button">Все задания</button>`)}<section class="v12-today-block"><header><span>Изменения</span><button type="button" data-x10-view="notifications">Все</button></header><div class="v12-change-list">${changesHTML}</div></section><nav class="x10-shortcuts">${contextRow({icon:'calendar',title:'Расписание',meta:'Неделя целиком',attrs:'data-x10-view="schedule"'})}${contextRow({icon:'book',title:'Учёба',meta:'Семестр и предметы',attrs:'data-x10-view="studyhub"'})}</nav></div>`;
  mountContextTopActions(root);
  $$('[data-open-lesson]',root).forEach((b)=>b.addEventListener('click',()=>showSchedule(b.dataset.openLesson)));
  $$('[data-x10-view]',root).forEach((b)=>b.addEventListener('click',()=>setView(b.dataset.x10View)));
  $$('[data-x10-task]',root).forEach((b)=>b.addEventListener('click',()=>b.dataset.entry?showSchedule(b.dataset.entry):setView(b.dataset.x10Task)));
  $$('[data-open-autopilot]',root).forEach(b=>b.addEventListener('click',()=>openAutopilotSettings().catch(e=>toast(e.message))));
  $$('[data-autopilot-run]',root).forEach(b=>b.addEventListener('click',()=>openAutopilotRun(b).catch(e=>toast(e.message))));
  $$('[data-notification-id]',root).forEach(b=>b.addEventListener('click',()=>openNotificationByID(b.dataset.notificationId,changes).catch(e=>toast(e.message))));
  mountLessonFocusShimmer(root);
}

export async function renderContextStudyHub() {
  resetHeadActions(); if(!requireGroup())return;
  const root=$('#content');
  const rows=[
    ['semester','calendar','Семестр','Общая картина текущего семестра'],
    ['courses','book','Предметы','Пары, задания, автомат, решения, конспекты и материалы'],
    ['homework','check','Задания','Открытые и выполненные задания'],
    ['requirements','progress','Автомат','Условия и личный прогресс'],
    ['solutions','check','Решения','Решения задач — свои, AI и от друзей'],
    ['notes','file','Конспекты','Файлы и записи по занятиям'],
    ['materials','link','Материалы','Методички, книги, файлы и полезные ссылки'],
    ['attendance','people','Посещаемость','Личные отметки и статистика'],
    ['skip','clock','Сообщить о пропуске','Предупредить старосту'],
  ];
  root.innerHTML=`<div class="x10-page"><header class="x10-page-head"><div><span class="x10-eyebrow">Учебный контекст</span><h2>Учёба</h2><p>Выбирай объект, а не раздел базы данных.</p></div><div class="x10-page-tools"><button class="x10-icon-button" type="button" data-x10-search aria-label="Поиск">${uiIcon('search')}</button>${(canManageGroup()||canUploadNotes())?`<button class="x10-primary-icon" type="button" data-x10-add aria-label="Добавить">${uiIcon('plus')}</button>`:''}</div></header><div class="x10-nav-list">${rows.map(([view,icon,title,meta])=>contextRow({icon,title,meta,attrs:`data-x10-view="${view}"`})).join('')}</div></div>`;
  mountContextTopActions(root); $$('[data-x10-view]',root).forEach((b)=>b.addEventListener('click',()=>setView(b.dataset.x10View)));
}

export async function renderContextMoreHub() {
  resetHeadActions();
  const rows=[['settings','edit','Настройки','Тема, интерфейс и аккаунт'],['teachers','people','Преподы','Файлы преподавателей и опыт студентов'],['wiki','book','Wiki','Инструкции и памятки студентов'],['events','calendar','Мероприятия','Встречи, конференции и хакатоны'],['council','message','Студсовет','Обращения и ответы вашего ВУЗа'],['notifications','circle','Уведомления','Изменения и напоминания'],['group','people','Моя группа','Участники и приглашения'],['navigator','progress','Навигатор','Корпуса, аудитории и места']];
  if(isAdmin())rows.push(['admin','edit','Администрирование','Роли и журнал действий']);
  const root=$('#content'); root.innerHTML=`<div class="x10-page"><header class="x10-page-head"><div><span class="x10-eyebrow">Остальное</span><h2>Ещё</h2></div><div class="x10-page-tools"><button class="x10-icon-button" type="button" data-x10-search aria-label="Поиск">${uiIcon('search')}</button></div></header><div class="x10-nav-list">${rows.map(([view,icon,title,meta])=>contextRow({icon,title,meta,attrs:`data-x10-view="${view}"`})).join('')}</div></div>`;
  mountContextTopActions(root); $$('[data-x10-view]',root).forEach((b)=>b.addEventListener('click',()=>setView(b.dataset.x10View)));
}

export function initAndroidUikitRuntime() {
  // This domain has no eager DOM side effects.
}
